"use client";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { formatYmdUk } from "@/lib/kyiv";
import { isAdmin, isStaff } from "@/lib/roles";
import type { MeResponse } from "@/lib/types";
import { StatusScreen } from "@/components/status-screen";
import { TimetableEditor } from "@/components/timetable-editor";

export function SettingsForm({ me }: { me: MeResponse }) {
  const [className, setClassName] = useState(me.settings.className);
  const [deadlineTime, setDeadlineTime] = useState(me.settings.deadlineTime);
  const [reminderTime, setReminderTime] = useState(me.settings.reminderTime);
  const [markOpenTime, setMarkOpenTime] = useState(me.settings.markOpenTime);

  if (!isStaff(me.user.role)) {
    return <StatusScreen title="Налаштування для класного керівника і адміністратора" />;
  }

  async function save() {
    try {
      await api("/api/settings", {
        method: "PATCH",
        body: JSON.stringify({ className, deadlineTime, reminderTime, markOpenTime }),
      });
      toast.success("Налаштування збережено. Нагадування підуть за київським часом.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося зберегти.");
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm leading-6 text-muted-foreground">
        Бот нагадує батькам, коли відкривається відмітка, вранці і за 10 хвилин до дедлайну. Двом дітям приходить один лист. Субота без нагадувань. У неділю — про понеділок. Свята й канікули додає адміністратор.
      </p>
      <Field label="Назва класу">
        <Input value={className} onChange={(event) => setClassName(event.target.value)} />
      </Field>
      <Field label="Нагадування вранці">
        <Input type="time" value={reminderTime} onChange={(event) => setReminderTime(event.target.value)} />
      </Field>
      <Field label="Відмітка на наступний ранок з">
        <Input type="time" value={markOpenTime} onChange={(event) => setMarkOpenTime(event.target.value)} />
      </Field>
      <Field label="Дедлайн і огляд дня">
        <Input type="time" value={deadlineTime} onChange={(event) => setDeadlineTime(event.target.value)} />
      </Field>
      <Button className="h-11 w-full rounded-2xl" onClick={() => void save()}>
        Зберегти
      </Button>
      <TimetableEditor />
      {isAdmin(me.user.role) ? <OffDaysEditor /> : null}
    </div>
  );
}

type OffDay = { id: string; startDate: string; endDate: string; label: string };

function OffDaysEditor() {
  const [days, setDays] = useState<OffDay[]>([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [label, setLabel] = useState("");
  const [loading, setLoading] = useState(true);

  async function load() {
    const data = await api<{ days: OffDay[] }>("/api/off-days");
    setDays(data.days);
    setLoading(false);
  }

  useEffect(() => {
    void load().catch((error) => {
      toast.error(error.message);
      setLoading(false);
    });
  }, []);

  async function add() {
    if (!startDate) {
      toast.error("Вкажіть дату або період.");
      return;
    }
    try {
      await api("/api/off-days", {
        method: "POST",
        body: JSON.stringify({
          startDate,
          endDate: endDate || startDate,
          label,
        }),
      });
      setStartDate("");
      setEndDate("");
      setLabel("");
      toast.success("Вихідний збережено. Цього дня бот мовчатиме.");
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося додати.");
    }
  }

  async function remove(id: string) {
    try {
      await api("/api/off-days", {
        method: "DELETE",
        body: JSON.stringify({ id }),
      });
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося видалити.");
    }
  }

  return (
    <section className="space-y-3 rounded-3xl bg-card p-4 ring-1 ring-foreground/8">
      <h2 className="text-sm font-medium">Свята і канікули</h2>
      <p className="text-sm leading-6 text-muted-foreground">
        Один день або період. Суботу й неділю додавати не треба.
      </p>
      <Input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
      <Input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
      <Input
        value={label}
        onChange={(event) => setLabel(event.target.value)}
        placeholder="Наприклад, зимові канікули"
      />
      <Button variant="outline" className="w-full rounded-2xl" onClick={() => void add()}>
        Додати вихідні
      </Button>
      {loading ? (
        <p className="text-xs text-muted-foreground">Завантажуємо календар…</p>
      ) : days.length === 0 ? (
        <p className="text-xs text-muted-foreground">Поки лише звичайні суботи й неділі.</p>
      ) : (
        <ul className="space-y-2">
          {days.map((day) => (
            <li key={day.id} className="flex items-start justify-between gap-3 text-sm">
              <span>
                {day.startDate === day.endDate
                  ? formatYmdUk(day.startDate)
                  : `${formatYmdUk(day.startDate)} — ${formatYmdUk(day.endDate)}`}
                {day.label ? ` · ${day.label}` : null}
              </span>
              <button
                type="button"
                className="text-xs font-medium text-primary"
                onClick={() => void remove(day.id)}
              >
                Прибрати
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-2 rounded-3xl bg-card p-4 ring-1 ring-foreground/8">
      <Label className="text-sm font-medium">{label}</Label>
      {children}
    </label>
  );
}
